import { ProjectDatesConstants } from "../../../../../../constants";
import { GetProjectDetailsResponse } from "../../../../../../service/publicquery";
import { Column, Divider, ReadonlyText, Row } from "../../../../../../widget";
import {
  buildClassifiedDateFields,
  buildSecondAssessmentDateField,
  buildUnclassifiedDateFields,
  ClassifiedDateFieldDescriptor,
  classifiedKeysOf,
  isDateSchemaEmpty,
  NOT_AVAILABLE,
  ReadonlyDateField,
} from "../../../../../shared/projects/project/overview/components/DatesSection/buildUnclassifiedDateFields";
import { useDatesSection } from "./useDatesSection";

interface DatesSectionProps {
  projectDetails: GetProjectDetailsResponse;
}

/**
 * Dates-group fields this panel renders explicitly, in display order (with the public panel's own
 * labels). This list is deliberately smaller than the private panel's — public has no explicit
 * Certification / Restoration validation fields — so those surface via {@link buildUnclassifiedDateFields}
 * instead of being dropped.
 */
const DATE_FIELD_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "design.registrationDate",
    label: "Registration date",
    tooltipText: ProjectDatesConstants.DESIGN_REGISTRATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.implementationDate",
    label: "Implementation date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_IMPLEMENTATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.startDate",
    label: "Start date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_START_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "duration.duration",
    label: "Project duration",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_DURATION_TOOLTIP_TEXT,
  },
];

/** Monitoring-group fields rendered before the derived second-assessment date. */
const PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "implementation.validationDate",
    label: "Validation date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_FIRST_ASSESSMENT_DATE_TOOLTIP_TEXT,
  },
];

/** Monitoring-group fields rendered after the derived second-assessment date. */
const POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "ongoingMonitoring.finalAssessmentDate",
    label: "Final assessment date",
    tooltipText: ProjectDatesConstants.ONGOING_MONITORING_FINAL_ASSESSMENT_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "ongoingMonitoring.subsequentFrequency",
    label: "Subsequent frequency",
    tooltipText: ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_TOOLTIP_TEXT,
  },
];

/** Derived from the descriptors so it never drifts from the fields actually rendered explicitly. */
const CLASSIFIED_DATE_KEYS = classifiedKeysOf(
  DATE_FIELD_DESCRIPTORS,
  PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS,
  POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS
);

const renderFieldRows = (fields: ReadonlyDateField[]): JSX.Element[] => {
  const rowCount = Math.ceil(fields.length / 2);

  return Array.from({ length: rowCount }, (_unused, rowIndex) => {
    const leftField = fields[rowIndex * 2];
    const rightField = fields[rowIndex * 2 + 1];

    return (
      <Row spacingV="ll" key={leftField.key}>
        <Column span={5}>
          <ReadonlyText
            label={leftField.label}
            value={leftField.value ?? NOT_AVAILABLE}
            noDataAvailable={!leftField.value}
            tooltipText={leftField.tooltipText}
            tooltipHeader=""
          />
        </Column>
        {rightField && (
          <Column span={5}>
            <ReadonlyText
              label={rightField.label}
              value={rightField.value ?? NOT_AVAILABLE}
              noDataAvailable={!rightField.value}
              tooltipText={rightField.tooltipText}
              tooltipHeader=""
            />
          </Column>
        )}
      </Row>
    );
  });
};

/**
 * Public (read-only) project dates. Keeps its own presentation (PublicDatesSectionWrapper, h2
 * headings, span-5 layout, no edit control) but now renders schema-first like the private overview:
 * each field only appears when the date schema declares it, and any schema date this panel doesn't
 * render explicitly is surfaced generically by buildUnclassifiedDateFields — so new schema dates show
 * up here without a code change, matching the private panel. The "Second assessment date" is derived
 * from the first monitoring cycle rather than a schema field, so it is shown only when that cycle has
 * an assessment date.
 *
 * The whole panel is hidden when the project has no date schema (see isDateSchemaEmpty), rather than
 * rendering empty headings.
 */
export const DatesSection = ({ projectDetails }: DatesSectionProps): JSX.Element | null => {
  const { projectDates, dateSchema } = useDatesSection({ projectDetails });

  if (isDateSchemaEmpty(dateSchema)) {
    return null;
  }

  // Explicit fields first, then any remaining schema date fields (e.g. certification date, or fields
  // newly added to the schema) surfaced generically so nothing is silently dropped from the public view.
  const datesFields: ReadonlyDateField[] = [
    ...buildClassifiedDateFields(dateSchema, projectDates, DATE_FIELD_DESCRIPTORS),
    ...buildUnclassifiedDateFields(dateSchema, projectDates, CLASSIFIED_DATE_KEYS),
  ];

  // The derived second-assessment date is interleaved between the validation date and the remaining
  // monitoring fields, preserving the previous ordering.
  const monitoringFields: ReadonlyDateField[] = [
    ...buildClassifiedDateFields(dateSchema, projectDates, PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS),
    ...buildSecondAssessmentDateField(projectDates),
    ...buildClassifiedDateFields(dateSchema, projectDates, POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS),
  ];

  return (
    <div className="PublicProjectTabSection">
      <div className="PublicDatesSectionWrapper">
        <Row spacingV="ll">
          <Column span={6}>
            <h2>Dates</h2>
          </Column>
        </Row>
        {renderFieldRows(datesFields)}
        <Row spacingV="xl">
          <Column span={12}>
            <Divider type="horizontal" />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={6}>
            <h2>Monitoring</h2>
          </Column>
        </Row>
        {renderFieldRows(monitoringFields)}
      </div>
    </div>
  );
};
